import React from 'react';

import { Select } from '@aline/common';
import type { AlineButtonProps } from 'src/components';
import { AlineButton } from 'src/components';
import { Apollo } from 'src/gql/generated';
import { useDocumentParams } from 'src/helpers';
import { useConvertToAline, useGetCurrentDoc } from 'src/hooks';
import { useEditorLoading } from 'src/layout-engine';

import { SendDocumentOutlinedButton, WorkflowButton } from './components';
import type { AppBarDocument } from './EditorAppBar';

interface DocumentAppBarButtonsProps {
  document: AppBarDocument;
}

export function DocumentAppBarButtons({
  document,
}: DocumentAppBarButtonsProps): JSX.Element | null {
  const editorLoading = useEditorLoading();
  const isContract = document.variant === Apollo.DocumentVariant.Document;

  if (!isContract) return null;

  return (
    <>
      <WorkflowButton disabled={editorLoading} />
      <SendDocumentOutlinedButton document={document} />
      <ConvertToAlineButton />
    </>
  );
}

interface ConvertToAlineButtonProps {
  buttonProps?: AlineButtonProps;
}

export function ConvertToAlineButton({
  buttonProps,
}: ConvertToAlineButtonProps): JSX.Element | null {
  const {
    convertToAline,
    getIsActivelyConverting,
    getIsDocumentConvertable,
    loading: convertingToAline,
  } = useConvertToAline();
  const { document } = useGetCurrentDoc();
  const editorLoading = useEditorLoading();
  const { documentId } = useDocumentParams();
  const isDocumentConverting = getIsActivelyConverting(document);
  const isDocumentConvertable = getIsDocumentConvertable(document);

  if (!isDocumentConvertable) return null;

  return (
    <AlineButton
      color="primary"
      data-cy={Select.document.editor.appBar.convertToAline.button}
      disabled={editorLoading}
      loading={convertingToAline || isDocumentConverting}
      variant="contained"
      onClick={async () => {
        await convertToAline(documentId);
      }}
      {...buttonProps}
    >
      Edit
    </AlineButton>
  );
}
